import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import HeroCarousel from "./components/HeroCarousel";
import { movies, featuredIds } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import {
  formatClicks,
  getFeatured,
  getMediaByType,
  getPopular,
} from "@/utils/media";

export default function Home() {
  const featured = getFeatured(featuredIds);
  const latest = [...movies]
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .slice(0, 8);
  const popular = getPopular(6);
  const latestNews = newsList.slice(0, 3);
  const movieCount = getMediaByType("movie").length;
  const tvCount = getMediaByType("tv").length;

  const entries = [
    {
      to: "/category/movie",
      label: "电影",
      desc: "院线新片 · 经典佳作 · 类型齐全",
      count: movieCount,
      icon: "ri-movie-2-line",
      image: movies[0].backdrop,
    },
    {
      to: "/category/tv",
      label: "电视剧",
      desc: "热播剧集 · 港台欧美 · 每日更新",
      count: tvCount,
      icon: "ri-tv-2-line",
      image: movies[2].backdrop,
    },
  ];

  return (
    <div className="w-full">
      <h1 className="sr-only">
        茶杯狐影视 - 精彩电影电视剧影视资源合集
      </h1>
      <HeroCarousel items={featured} />

      {/* 分类快捷入口 */}
      <section className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <SectionHeading
          title="分类快捷入口"
          subtitle="按类型快速进入，找到你想看的每一部"
          icon="ri-layout-grid-line"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
          {entries.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="group relative flex h-40 items-end overflow-hidden rounded-lg border border-background-200 md:h-48"
            >
              <img
                src={entry.image}
                alt={`${entry.label}分类`}
                title={`${entry.label} - 茶杯狐影视`}
                className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-transparent" />
              <div className="relative z-10 flex w-full items-center justify-between p-5 md:p-6">
                <div>
                  <div className="flex items-center gap-2 text-background-50">
                    <i className={`${entry.icon} text-2xl text-accent-400`} aria-hidden="true" />
                    <h3 className="font-heading text-xl font-bold md:text-2xl">
                      {entry.label}
                    </h3>
                  </div>
                  <p className="mt-2 text-sm text-background-200">{entry.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-100 backdrop-blur-sm">
                    共 {entry.count} 部作品
                  </span>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-500 text-accent-950 transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line text-xl" aria-hidden="true" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新影视资源 */}
      <section className="bg-background-100/70">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <SectionHeading
            title="最新影视资源"
            subtitle="最新上线与更新中的影视作品"
            icon="ri-time-line"
            moreTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {latest.map((item, index) => (
              <MovieCard key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* 热门推荐 + 最新资讯 */}
      <section className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading
              title="热门推荐"
              subtitle="本月点击量最高的影视作品"
              icon="ri-fire-line"
              moreTo="/category/movie"
            />
            <ol className="space-y-3">
              {popular.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-accent-300 hover:bg-accent-50"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold ${
                        index < 3
                          ? "bg-accent-500 text-accent-950"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={item.cover}
                        alt={`${item.title}封面`}
                        title={`${item.title} - 影视详情`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                        {item.title}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                        <span>{item.year}</span>
                        <span className="h-1 w-1 rounded-full bg-foreground-300" />
                        <span>{item.region}</span>
                        <span className="h-1 w-1 rounded-full bg-foreground-300" />
                        <span>{(item.genres || []).join(" · ")}</span>
                      </div>
                      <p className="mt-1.5 line-clamp-1 text-xs text-foreground-400">
                        {item.synopsis}
                      </p>
                    </div>
                    <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                      <span className="flex items-center gap-1 text-sm font-bold text-accent-600">
                        <i className="ri-star-fill text-xs" aria-hidden="true" />
                        {item.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-fire-line" aria-hidden="true" />
                        {formatClicks(item.clicks)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="lg:col-span-1">
            <SectionHeading
              title="最新影视资讯"
              subtitle="行业动态与影视热点"
              icon="ri-newspaper-line"
              moreTo="/news"
            />
            <div className="flex flex-col gap-3">
              {latestNews.map((item, index) => (
                <NewsCard key={item.id} item={item} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}